import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type RouteParams } from './RouteParams';
import { featureRouteMap } from './featureRouteMap';
import { serializeWebHistoryState } from './serializeWebHistoryState';

// add two type definitions for navigate: (1) for known routes from RouteParams and (2) computed or dynamic URLs
type KnownRoute = keyof RouteParams;
const isKnownRoute = (route: string): route is KnownRoute => {
  return route in featureRouteMap;
};

type NavigateFunction = {
  <NextRouteName extends KnownRoute>(route: NextRouteName, params?: RouteParams[NextRouteName]): void;
  (route: string, params?: any): void;
};

export const useNavigation = <RouteName extends keyof RouteParams = 'RouteAppTabNavigator'>() => {
  const navigateRRD = useNavigate();
  const location = useLocation();

  const navigate = useCallback<NavigateFunction>(
    (route: string, params?: any) => {
      let featureRoute;
      if (route === 'RouteAppTabNavigator' && params && 'screen' in params) {
        const tabRouteName: KnownRoute = params.screen;
        if (!tabRouteName) return;
        // pass route params for tabs that have them
        if (tabRouteName === 'RouteTabMyMeds') {
          featureRoute = featureRouteMap[tabRouteName](params as RouteParams['RouteTabMyMeds']).web;
        } else {
          featureRoute = featureRouteMap[tabRouteName]().web;
        }
      } else if (isKnownRoute(route)) {
        featureRoute = featureRouteMap[route](params).web;
      } else {
        featureRoute = route;
      }
      // Support path-only navigation with optional replace (e.g. AuthRequired: navigate(nextPath, { replace: true }))
      const replaceHistory = params && typeof params === 'object' && 'replace' in params && params.replace === true;
      const rawState =
        replaceHistory && params && typeof params === 'object'
          ? (() => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit replace from state
              const { replace, ...rest } = params;
              return Object.keys(rest).length ? rest : undefined;
            })()
          : params;
      const state = serializeWebHistoryState(rawState);
      navigateRRD(featureRoute, { replace: !!replaceHistory, state });
    },
    [navigateRRD],
  );

  const replace = useCallback(
    <T extends keyof RouteParams>(routeName: T, replaceParams?: RouteParams[T]) => {
      const featureRoute = featureRouteMap[routeName](replaceParams).web;
      navigateRRD(featureRoute, { replace: true });
    },
    [navigateRRD],
  );

  const goBack = useCallback(
    (_key?: string) => {
      void _key;
      if (window.history.length > 1) {
        navigateRRD(-1);
      } else {
        navigateRRD('/');
      }
    },
    [navigateRRD],
  );

  return {
    /**
     * @param route The native route name that you want to navigate to. Found in libs/shared/lib/nativeRouteNames.ts
     * @param params The params you want to pass. The relationship between the route name and passable params is found in libs/navigation/src/featureRouteMap.ts
     */
    navigate,

    /**
     * Get the name of the currently-active route
     */
    getCurrentRouteName: () => {
      return location?.pathname;
    },

    /**
     * Get the value a URL parameter in a search string (i.e. '?next=/prescriptions') or
     * from location state
     */
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
    getParam: <RouteKey extends keyof RouteParams[RouteName]>(paramName: RouteKey, fallbackValue?: any) => {
      const params = new URLSearchParams(location.search);
      const paramValue = location.state?.[paramName];
      return params.get(paramName as string) || paramValue || fallbackValue;
    },

    /**
     * No-op on web for now.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setParams: (_params?: Record<string, any>) => undefined,

    /**
     * No-op on web for now.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setOptions: (_options: any) => undefined,

    /**
     * Go to the previous page. NOTE this takes a key arg to maintain react-native compatibility but this does nothing
     * on web, it will always instead take you to the previous page.
     */
    goBack,

    /**
     * No-op on web for now.
     */
    canGoBack: () => undefined,

    getRouteName: (routeName: RouteName) => routeName,

    /**
     * @param route The native route name that you want to navigate to. Found in libs/shared/lib/nativeRouteNames.ts
     * @param params The params you want to pass. The relationship between the route name and passable params is found in libs/navigation/src/featureRouteMap.ts
     */
    replace,
  };
};

export type Navigate = ReturnType<typeof useNavigation>['navigate'];
